/*
  Kkumi bundle cart guard — a Simple Bundles (Infinite Options) bundle must
  never reach checkout without its choices.

  Why: a configured bundle is its parent variant plus line properties (kit,
  Galaxy colour, bag colour, `_bundle_selection`). Simple Bundles builds the
  components from them at checkout. A line that lost them — re-added from a
  path that only kept the variant, an old cart, a cart link — checks out as the
  bare parent at its tweezer price with NO components, and Shopify shows no
  error (no checkout validation without Plus). The theme catches it in the cart.

  Detection is server-side: snippets/kkumi-sb-cart-check.liquid compares each
  line's properties with the app's own `simple_bundles.variant_options`
  metafield and renders a notice marked `data-kk-sb-incomplete`. This script:
    1. blocks checkout from a surface (cart page / drawer) while it holds such a
       line: checkout clicks and submits are stopped (window, capture phase), the
       button reads as unavailable and the wallet buttons hide (CSS keyed on
       html[data-kk-sb-blocked-page|drawer]), and a note explains why;
    2. wires the notice's fix button: remove that line (/cart/change.js), then
       open the product page to choose again (KKBtn busy state meanwhile; it
       navigates even if the remove fails — the guard still holds).
  It never touches `disabled` on the checkout buttons, so it can't fight
  kkumi-greenlight-guard.js or Dawn's empty-cart state over that attribute.
  Backorder acknowledgements have their own handler and never use bundle repair.
  Checkout evidence errors stay on the current surface with a retry action.
  Text is written with textContent only.
*/
(function () {
  if (window.__kkSbCartGuard) return;
  window.__kkSbCartGuard = true;

  var SURFACES = [
    { key: 'page', scope: function () { return document.getElementById('main-cart-items'); }, button: '#checkout', host: '.kkumi-summary__checkout' },
    { key: 'drawer', scope: function () { return document.querySelector('cart-drawer'); }, button: '#CartDrawer-Checkout', host: 'cart-drawer .cart__ctas' }
  ];

  function issueIn(surface) {
    var scope = surface.scope();
    return scope ? scope.querySelector('[data-kk-sb-incomplete], [data-kk-backorder-incomplete]') : null;
  }

  function surfaceFor(el) {
    if (el.closest && (el.closest('cart-drawer') || el.id === 'CartDrawer-Checkout')) return SURFACES[1];
    return SURFACES[0];
  }

  function apply() {
    var html = document.documentElement;
    SURFACES.forEach(function (s) {
      var issue = issueIn(s);
      var attr = 'data-kk-sb-blocked-' + s.key;
      if (!!issue !== html.hasAttribute(attr)) {
        if (issue) html.setAttribute(attr, '');
        else html.removeAttribute(attr);
      }
      document.querySelectorAll(s.button).forEach(function (btn) {
        if (issue) {
          if (btn.getAttribute('aria-disabled') !== 'true') btn.setAttribute('aria-disabled', 'true');
          btn.dataset.kkSbBlocked = '1';
        } else if (btn.dataset.kkSbBlocked) {
          delete btn.dataset.kkSbBlocked;
          // Leave aria-disabled alone when the greenlight guard owns it too.
          if (!btn.dataset.glDisabled) btn.removeAttribute('aria-disabled');
        }
      });
      if (s.key === 'page') {
        var hasBackorder = !!document.querySelector('#main-cart-items [data-kk-cart-backorder]');
        document.querySelectorAll('[data-kk-cart-express]').forEach(function (el) { el.hidden = hasBackorder; });
      }
      document.querySelectorAll(s.host).forEach(function (host) {
        var note = host.querySelector('.kk-sb-cart-guard__note:not(#KkCartCheckError)');
        if (!issue) { if (note) note.remove(); return; }
        var text = issue.getAttribute('data-blocked-note') || '';
        if (!note) {
          note = document.createElement('p');
          note.className = 'kk-sb-cart-guard__note';
          note.setAttribute('role', 'alert');
          host.insertBefore(note, host.firstChild);
        }
        if (note.textContent !== text) note.textContent = text;
      });
    });
  }

  function stop(e, surface) {
    var issue = issueIn(surface);
    if (!issue) return false;
    e.preventDefault();
    e.stopImmediatePropagation();
    apply();
    if (issue.scrollIntoView) issue.scrollIntoView({ block: 'center', behavior: 'smooth' });
    var details = issue.querySelector('details');
    if (details) details.open = true;
    var fix = issue.querySelector('[data-kk-backorder-ack], [data-kk-sb-fix]');
    if (fix) fix.focus({ preventScroll: true });
    return true;
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // Missing stock evidence is an error, not a confirmed blocked item.
  var STATUS_SECTION = 'kkumi-sb-cart-status';
  function liveCheck() {
    var controller = new AbortController();
    var timer;
    var request = fetch(root() + 'cart?section_id=' + STATUS_SECTION, {
      credentials: 'same-origin', cache: 'no-store', signal: controller.signal
    }).then(function (r) {
      if (!r.ok) throw new Error('response');
      return r.text();
    }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var proof = doc.querySelector('[data-kk-sb-cart-status][data-version="1"]');
      if (!proof || !/^\d+$/.test(proof.dataset.itemCount || '')) throw new Error('response');
      var issue = proof.querySelector('[data-kk-sb-incomplete], [data-kk-backorder-incomplete]');
      return { status: issue ? 'blocked' : 'clear', issue: issue };
    }).catch(function (error) { return { status: 'error', reason: error.message === 'response' ? 'response' : 'network' }; });
    var timeout = new Promise(function (resolve) {
      timer = setTimeout(function () { resolve({ status: 'error', reason: 'timeout' }); controller.abort(); }, 3000);
    });
    return Promise.race([request, timeout]).finally(function () { clearTimeout(timer); });
  }

  function diagnostic(name, trigger, reason) {
    if (!window.KKCheckoutDiagnostics) return;
    window.KKCheckoutDiagnostics(name, trigger.tagName === 'A' ? 'link' : surfaceFor(trigger).key, reason);
  }

  function checkError(trigger) {
    var config = document.querySelector('[data-kk-cart-flow-config]');
    var copy = config ? config.dataset : {};
    var previous = document.getElementById('KkCartCheckError');
    if (previous) previous.remove();
    var panel = document.createElement('div');
    panel.id = 'KkCartCheckError';
    panel.className = 'kk-sb-cart-guard__note';
    panel.setAttribute('role', 'alert');
    var message = document.createElement('p');
    message.textContent = copy.checkError || '';
    var retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button button--secondary';
    retry.textContent = copy.checkRetry || '';
    retry.addEventListener('click', function (event) { guard(event, trigger); });
    panel.append(message, retry);
    var surface = surfaceFor(trigger);
    var host = document.querySelector(surface.host);
    if (host) host.appendChild(panel);
    else if (trigger.isConnected) trigger.insertAdjacentElement('afterend', panel);
    else document.body.appendChild(panel);
    retry.focus({ preventScroll: true });
  }

  // A link straight to checkout (the toast's /cart/checkout, any /checkout link).
  function isCheckoutLink(el) {
    if (!el || el.tagName !== 'A' || !el.href) return false;
    try {
      var path = new URL(el.href, window.location.href).pathname.replace(/\/+$/, '');
      return /\/checkout$/.test(path) || /\/checkouts?\//.test(path);
    } catch (e) { return false; }
  }

  var bypass = false; // set while re-issuing a checkout the live check cleared
  var pending = false;
  var checkGeneration = 0;

  function proceed(trigger) {
    bypass = true;
    try {
      if (trigger.tagName === 'A') {
        window.location.href = trigger.href;
      } else if (trigger.tagName === 'FORM') {
        if (trigger.requestSubmit) trigger.requestSubmit(); else trigger.submit();
      } else if (trigger.form && trigger.form.requestSubmit) {
        trigger.form.requestSubmit(trigger);
      } else {
        trigger.click();
      }
    } finally {
      setTimeout(function () { bypass = false; }, 0);
    }
  }

  function guard(e, trigger) {
    if (bypass) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (pending || window.KKCartBackorder && window.KKCartBackorder.busy) return;
    pending = true;
    var generation = ++checkGeneration;
    trigger.dataset.kkCheckPending = '1';
    diagnostic('checkout_attempt', trigger);
    var panel = document.getElementById('KkCartCheckError');
    if (panel) panel.remove();
    var config = document.querySelector('[data-kk-cart-flow-config]');
    var useBusy = trigger.tagName === 'BUTTON' && window.KKBtn;
    if (useBusy) window.KKBtn.busy(trigger, config && config.dataset.checkBusy || '');
    else trigger.setAttribute('aria-busy', 'true');
    liveCheck().then(function (result) {
      if (generation !== checkGeneration) return;
      delete trigger.dataset.kkCheckPending;
      pending = false;
      if (useBusy) window.KKBtn.reset(trigger);
      else trigger.removeAttribute('aria-busy');
      if (!trigger.isConnected) {
        diagnostic('checkout_check_failed', trigger, 'changed');
        checkError(document.querySelector(surfaceFor(trigger).button) || trigger);
        return;
      }
      if (result.status === 'error') {
        diagnostic('checkout_check_failed', trigger, result.reason);
        checkError(trigger);
        return;
      }
      if (result.status === 'blocked') {
        diagnostic(result.issue.hasAttribute('data-kk-backorder-incomplete') ? 'checkout_blocked_backorder' : 'checkout_blocked_bundle', trigger);
        if (!stop(e, surfaceFor(trigger))) window.location.href = root() + 'cart';
        return;
      }
      // Replenishment or an acknowledgement in another surface can make local
      // issue markup stale. Fresh server proof clears this guard for this attempt.
      proceed(trigger);
    });
  }

  // Window + capture: runs before any theme or app handler on the checkout
  // controls (global.js and the greenlight guard listen on document).
  window.addEventListener('click', function (e) {
    var t = e.target && e.target.closest && e.target.closest('#checkout, #CartDrawer-Checkout, button[name="checkout"], a[href]');
    if (!t) return;
    if (t.tagName === 'A' && !isCheckoutLink(t)) return;
    guard(e, t);
  }, true);
  window.addEventListener('submit', function (e) {
    var sub = e.submitter;
    var form = e.target;
    var isCheckout = (sub && sub.name === 'checkout') ||
      (form && (form.id === 'cart' || form.id === 'CartDrawer-Form') && !(sub && sub.name === 'update'));
    if (isCheckout) guard(e, sub || form);
  }, true);

  // Fix: remove the broken line, then open its product page to choose again.
  document.addEventListener('click', function (e) {
    var fix = e.target && e.target.closest && e.target.closest('[data-kk-sb-fix]');
    if (!fix || fix.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();
    var href = fix.getAttribute('data-href') || '/';
    if (window.KKBtn) window.KKBtn.busy(fix, fix.getAttribute('data-busy-label') || '');
    else fix.setAttribute('aria-busy', 'true');
    fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: fix.getAttribute('data-line-key'), quantity: 0 })
    })
      .catch(function () {})
      .then(function () { window.location.href = href; });
  });

  // Cart sections re-render on every change; re-apply once the new markup is in.
  var timer = null;
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(apply, 60);
  }
  function watch() {
    SURFACES.forEach(function (s) {
      var scope = s.scope();
      if (scope) new MutationObserver(schedule).observe(scope, { childList: true, subtree: true });
    });
    var footer = document.getElementById('main-cart-footer');
    if (footer) new MutationObserver(schedule).observe(footer, { childList: true, subtree: true });
  }

  function init() {
    apply();
    watch();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
    }
    document.addEventListener('kkumi:cart-reviewed', apply);
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      checkGeneration++;
      pending = false; bypass = false;
      document.querySelectorAll('[data-kk-check-pending]').forEach(function (trigger) {
        delete trigger.dataset.kkCheckPending;
        if (trigger.tagName === 'BUTTON' && window.KKBtn) window.KKBtn.reset(trigger);
        else trigger.removeAttribute('aria-busy');
      });
      apply();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
